import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ChevronRight,
  Search,
  Building,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  PlusCircle,
  Folder,
  MapPin,
  User,
  CalendarDays,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { GetProjectsResponseDto } from "@/dto/get-projects-response.dto";
import { ProjectStatus } from "@/enums/project.enum";
import { useDeviceType } from "@/hooks/use-mobile";
import {
  MobileContainer,
  MobileSpacing,
} from "@/components/mobile/ResponsiveLayout";
import MobileCard from "@/components/mobile/MobileCard";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";

type SortField =
  | "createdAt"
  | "updatedAt"
  | "name"
  | "ahj"
  | "companyName"
  | "submissions"
  | null;
type SortDirection = "asc" | "desc" | null;

interface ProjectsTableProps {
  projects: GetProjectsResponseDto[];
  isLoading: boolean;
  title?: string;
  description?: string;
  showNewButton?: boolean;
}

const ProjectsTable = ({
  projects,
  isLoading = false,
  title,
  description,
  showNewButton = false,
}: ProjectsTableProps) => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<ProjectStatus[]>([]);
  const [selectedAHJs, setSelectedAHJs] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const highlightedProjectId = searchParams.get("highlightedProject");
  const highlightedStatusId = searchParams.get("highlightedStatus");

  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
  const statusRefs = useRef<{ [key: string]: HTMLSpanElement | null }>({});

  // Initialize status filter from URL query parameter
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      // Split by comma to support multiple statuses
      const statuses = statusParam.split(",");
      const validStatuses: ProjectStatus[] = [];

      statuses.forEach((status) => {
        // Convert URL parameter to ProjectStatus enum value
        // Replace underscores with spaces to match enum values like "in progress" and "on hold"
        const formattedStatus = status.replace(/_/g, " ").toLowerCase();

        // Check if the formatted status exists in ProjectStatus enum values
        if (
          Object.values(ProjectStatus).includes(
            formattedStatus as ProjectStatus
          )
        ) {
          validStatuses.push(formattedStatus as ProjectStatus);
        }
      });

      if (validStatuses.length > 0) {
        setSelectedStatuses(validStatuses);
      }
    } else {
      setSelectedStatuses([]);
      // If no status param is present, also clear other filters if they were set
      // This assumes that navigating to /projects (Total) should clear everything
      if (searchParams.toString() === "") {
        setSelectedAHJs([]);
        setSelectedCompanies([]);
        setSearchTerm("");
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (highlightedProjectId && rowRefs.current[highlightedProjectId]) {
      rowRefs.current[highlightedProjectId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("highlightedProject");
      setTimeout(() => {
        navigate({ search: newParams.toString() }, { replace: true });
      }, 3000);
    }
    if (highlightedStatusId && statusRefs.current[highlightedStatusId]) {
      statusRefs.current[highlightedStatusId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("highlightedStatus");
      setTimeout(() => {
        navigate({ search: newParams.toString() }, { replace: true });
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate days remaining until review date
  const calculateDaysUntilReview = (
    reviewDate: Date | string | undefined
  ): number | null => {
    if (!reviewDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const review = new Date(reviewDate);
    review.setHours(0, 0, 0, 0);

    const diffTime = review.getTime() - today.getTime();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return daysRemaining;
  };

  // Get badge variant based on days remaining
  const getReviewDateBadgeVariant = (
    daysRemaining: number
  ): "default" | "warning" | "destructive" | "secondary" => {
    if (daysRemaining <= 1) return "destructive";
    if (daysRemaining <= 3) return "warning";
    return "secondary";
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 " />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="h-4 w-4 " />;
    }
    return <ArrowDown className="h-4 w-4" />;
  };

  // Get unique AHJ options
  const uniqueAHJs = Array.from(
    new Set(
      projects
        .filter((p) => p.ahj?.name)
        .map((p) => `${p.ahj.name} (${p.ahj.state})`)
    )
  ).sort();

  // Get unique Company Names
  const uniqueCompanies = Array.from(
    new Set(
      projects
        .filter((p) => p.createdBy?.companyName)
        .map((p) => p.createdBy.companyName)
    )
  ).sort();

  const allStatuses = [
    ProjectStatus.DRAFT,
    ProjectStatus.PENDING,
    ProjectStatus.SUBMITTED,
    ProjectStatus.IN_PROGRESS,
    ProjectStatus.COMPLETED,
    ProjectStatus.ON_HOLD,
    ProjectStatus.REJECTED,
  ];

  const filteredProjects = projects.filter((proj) => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      !searchTerm ||
      proj?.name?.toLowerCase().includes(searchLower) ||
      proj?.ahj?.name?.toLowerCase().includes(searchLower) ||
      proj?.createdBy?.companyName?.toLowerCase().includes(searchLower);

    // Status filter - show all projects when no status is selected (Total view)
    const matchesStatus =
      selectedStatuses.length === 0
        ? true
        : selectedStatuses.includes(proj.status);

    // AHJ filter
    const ahjString = proj.ahj ? `${proj.ahj.name} (${proj.ahj.state})` : null;
    const matchesAHJ =
      selectedAHJs.length === 0 ||
      (ahjString && selectedAHJs.includes(ahjString));

    // Company filter
    const matchesCompany =
      selectedCompanies.length === 0 ||
      (proj.createdBy?.companyName &&
        selectedCompanies.includes(proj.createdBy.companyName));

    return matchesSearch && matchesStatus && matchesAHJ && matchesCompany;
  });

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (!sortField || !sortDirection) return 0;

    let aValue: string | number;
    let bValue: string | number;

    if (sortField === "createdAt" || sortField === "updatedAt") {
      aValue = new Date(a[sortField]).getTime();
      bValue = new Date(b[sortField]).getTime();
    } else if (sortField === "name") {
      aValue = a.name?.toLowerCase() || "";
      bValue = b.name?.toLowerCase() || "";
    } else if (sortField === "ahj") {
      aValue = a.ahj?.name?.toLowerCase() || "";
      bValue = b.ahj?.name?.toLowerCase() || "";
    } else if (sortField === "companyName") {
      aValue = a.createdBy?.companyName?.toLowerCase() || "";
      bValue = b.createdBy?.companyName?.toLowerCase() || "";
    } else if (sortField === "submissions") {
      aValue = a.submission ? 1 : 0;
      bValue = b.submission ? 1 : 0;
    } else {
      return 0;
    }

    if (sortDirection === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    }
    return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
  });

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadgeVariant = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.PENDING:
        return "default";
      case ProjectStatus.ON_HOLD:
        return "warning";
      case ProjectStatus.REJECTED:
        return "destructive";
      case ProjectStatus.COMPLETED:
        return "success";
      default:
        return "outline";
    }
  };

  const toggleStatus = (status: ProjectStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const toggleAHJ = (ahj: string) => {
    setSelectedAHJs((prev) =>
      prev.includes(ahj) ? prev.filter((a) => a !== ahj) : [...prev, ahj]
    );
  };

  const toggleCompany = (company: string) => {
    setSelectedCompanies((prev) =>
      prev.includes(company)
        ? prev.filter((c) => c !== company)
        : [...prev, company]
    );
  };

  const formatStatusLabel = (status: ProjectStatus) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ");
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.PENDING:
        return "bg-yellow-100 text-yellow-800";
      case ProjectStatus.SUBMITTED:
        return "bg-blue-100 text-blue-800";
      case ProjectStatus.IN_PROGRESS:
        return "bg-purple-100 text-purple-800";
      case ProjectStatus.COMPLETED:
        return "bg-green-100 text-green-800";
      case ProjectStatus.ON_HOLD:
        return "bg-orange-100 text-orange-800";
      case ProjectStatus.REJECTED:
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Mobile view
  if (isMobile) {
    const activeFilterCount =
      selectedStatuses.length + selectedAHJs.length + selectedCompanies.length;

    return (
      <div className="space-y-4">
        {/* Header with title and button */}
        <div className="flex items-center justify-between">
          <div>
            {title && <h2 className="text-xl font-semibold">{title}</h2>}
            {description && (
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>
          {showNewButton && (
            <Button
              size="sm"
              onClick={() => navigate("/projects/new")}
              className="h-8"
            >
              <PlusCircle className="mr-1 h-4 w-4" />
              New
            </Button>
          )}
        </div>

        {/* Search Bar and Filter Button */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={`relative ${
                  activeFilterCount > 0 ? "border-primary" : ""
                }`}
              >
                <Filter className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="h-[50vh] rounded-t-3xl overflow-hidden"
            >
              <SheetHeader className="text-left">
                <SheetTitle>Filters</SheetTitle>
                <SheetDescription>
                  Apply filters to refine your project list
                </SheetDescription>
              </SheetHeader>

              <div className="py-4 space-y-4 overflow-y-auto h-[calc(50vh-140px)] overscroll-contain">
                {/* Status Filter Section - Horizontal Scrollable Chips */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm">Status</h3>
                    {selectedStatuses.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedStatuses([])}
                        className="h-auto py-1 px-2 text-xs"
                      >
                        Clear ({selectedStatuses.length})
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                    {allStatuses.map((status) => (
                      <Badge
                        key={status}
                        variant={
                          selectedStatuses.includes(status)
                            ? "default"
                            : "outline"
                        }
                        className="cursor-pointer whitespace-nowrap hover:bg-primary/90 transition-colors"
                        onClick={() => toggleStatus(status)}
                      >
                        {formatStatusLabel(status)}
                        {selectedStatuses.includes(status) && (
                          <X className="ml-1 h-3 w-3" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Company Name Filter Section - Horizontal Scrollable Chips */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm">Company Name</h3>
                    {selectedCompanies.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedCompanies([])}
                        className="h-auto py-1 px-2 text-xs"
                      >
                        Clear ({selectedCompanies.length})
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                    {uniqueCompanies.map((company) => (
                      <Badge
                        key={company}
                        variant={
                          selectedCompanies.includes(company)
                            ? "default"
                            : "outline"
                        }
                        className="cursor-pointer whitespace-nowrap hover:bg-primary/90 transition-colors"
                        onClick={() => toggleCompany(company)}
                      >
                        {company}
                        {selectedCompanies.includes(company) && (
                          <X className="ml-1 h-3 w-3" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* AHJ Filter Section - Horizontal Scrollable Chips */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm">AHJ</h3>
                    {selectedAHJs.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedAHJs([])}
                        className="h-auto py-1 px-2 text-xs"
                      >
                        Clear ({selectedAHJs.length})
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                    {uniqueAHJs.map((ahj) => (
                      <Badge
                        key={ahj}
                        variant={
                          selectedAHJs.includes(ahj) ? "default" : "outline"
                        }
                        className="cursor-pointer whitespace-nowrap hover:bg-primary/90 transition-colors"
                        onClick={() => toggleAHJ(ahj)}
                      >
                        {ahj}
                        {selectedAHJs.includes(ahj) && (
                          <X className="ml-1 h-3 w-3" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Sort Section */}
                <div className="space-y-2">
                  <h3 className="font-medium text-sm">Sort By</h3>
                  <div className="space-y-2">
                    {[
                      { field: "name" as SortField, label: "Project Name" },
                      { field: "ahj" as SortField, label: "AHJ" },
                      {
                        field: "companyName" as SortField,
                        label: "Company Name",
                      },
                      {
                        field: "createdAt" as SortField,
                        label: "Created Date",
                      },
                      {
                        field: "updatedAt" as SortField,
                        label: "Last Updated",
                      },
                      {
                        field: "submissions" as SortField,
                        label: "Submissions",
                      },
                    ].map(({ field, label }) => (
                      <div
                        key={field}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-accent cursor-pointer"
                        onClick={() => handleSort(field)}
                      >
                        <span className="text-sm">{label}</span>
                        <div className="flex items-center gap-1">
                          {sortField === field && sortDirection === "asc" && (
                            <>
                              <ArrowUp className="h-4 w-4 text-primary" />
                              <span className="text-xs text-primary">
                                Ascending
                              </span>
                            </>
                          )}
                          {sortField === field && sortDirection === "desc" && (
                            <>
                              <ArrowDown className="h-4 w-4 text-primary" />
                              <span className="text-xs text-primary">
                                Descending
                              </span>
                            </>
                          )}
                          {sortField !== field && (
                            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    ))}
                    {sortField && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSortField(null);
                          setSortDirection(null);
                        }}
                        className="w-full mt-2"
                      >
                        Clear Sort
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <SheetFooter className="absolute bottom-0 left-0 right-0 p-6 bg-background border-t">
                <div className="flex gap-2 w-full">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setSelectedStatuses([]);
                      setSelectedAHJs([]);
                      setSelectedCompanies([]);
                    }}
                  >
                    Clear All
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => setIsFilterOpen(false)}
                  >
                    Apply Filters
                  </Button>
                </div>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>

        {/* Projects List */}
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <MobileCard key={i} className="animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </MobileCard>
            ))}
          </div>
        ) : sortedProjects.length === 0 ? (
          <MobileCard className="text-center py-8">
            <Folder className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <h3 className="font-medium mb-1">No projects found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {searchTerm ||
              selectedStatuses.length > 0 ||
              selectedAHJs.length > 0
                ? "No projects match your filters"
                : "Create your first project to get started"}
            </p>
            {showNewButton && (
              <Button size="sm" onClick={() => navigate("/projects/new")}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Create Project
              </Button>
            )}
          </MobileCard>
        ) : (
          <div className="space-y-3">
            {sortedProjects.map((project) => (
              <MobileCard
                key={project._id}
                onClick={() => navigate(`/projects/${project._id}`)}
                className="p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-primary/10 rounded-lg">
                    <Folder className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-base leading-5 truncate">
                        {project.name}
                      </h3>
                      <Badge
                        className={`text-xs ${getStatusColor(
                          project.status
                        )} flex-shrink-0`}
                      >
                        {formatStatusLabel(project.status)}
                      </Badge>
                    </div>

                    <div className="space-y-1 text-sm text-muted-foreground">
                      {project.ahj && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">
                            {project.ahj.name} ({project.ahj.state})
                          </span>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">
                          {project.createdBy.companyName || "—"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-3 w-3 flex-shrink-0" />
                        <span>{formatDate(project.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </MobileCard>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Desktop view
  return (
    <div className="rounded-xl border border-border/50 bg-card shadow-sophisticated overflow-hidden">
      <div className="p-6 border-b border-border/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-muted/5">
        <div className="flex items-center gap-2">
          <div className="h-8 w-1 bg-primary rounded-full" />
          <div>
            {title && <h2 className="text-lg font-semibold text-foreground">{title}</h2>}
            {description && (
              <p className="text-sm text-muted-foreground">
                {description}
              </p>
            )}
            {!title && !description && <h2 className="text-lg font-semibold text-foreground">Projects</h2>}
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-background/50 border-border/50 focus:bg-background transition-all"
            />
          </div>
          {showNewButton && (
            <Button 
              onClick={() => navigate("/projects/new")}
              className="btn-gradient shadow-md hover:shadow-lg transition-all"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              New Project
            </Button>
          )}
        </div>
      </div>

      <div className="p-0">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="hover:bg-transparent border-border/40">
              <TableHead className="pl-6">
                <div className="flex items-center whitespace-nowrap">
                  Project Name
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-2 text-muted-foreground hover:text-foreground"
                    onClick={() => handleSort("name")}
                  >
                    {getSortIcon("name")}
                    <span className="sr-only">Sort by Project Name</span>
                  </Button>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center whitespace-nowrap">
                  AHJ
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ml-2 relative ${
                          selectedAHJs.length > 0
                            ? "text-primary"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Filter className="h-4 w-4" />
                        {selectedAHJs.length > 0 && (
                          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-medium">
                            {selectedAHJs.length}
                          </span>
                        )}
                        <span className="sr-only">Filter by AHJ</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64" align="start">
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        <div className="font-medium text-sm mb-3">
                          Filter by AHJ
                        </div>
                        {uniqueAHJs.map((ahj) => (
                          <div
                            key={ahj}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`ahj-${ahj}`}
                              checked={selectedAHJs.includes(ahj)}
                              onCheckedChange={() => toggleAHJ(ahj)}
                            />
                            <label
                              htmlFor={`ahj-${ahj}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {ahj}
                            </label>
                          </div>
                        ))}
                        {selectedAHJs.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full mt-2"
                            onClick={() => setSelectedAHJs([])}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center whitespace-nowrap">
                  Company Name
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-2 text-muted-foreground hover:text-foreground"
                    onClick={() => handleSort("companyName")}
                  >
                    {getSortIcon("companyName")}
                    <span className="sr-only">Sort by Company Name</span>
                  </Button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ml-1 relative ${
                          selectedCompanies.length > 0
                            ? "text-primary"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Filter className="h-4 w-4" />
                        {selectedCompanies.length > 0 && (
                          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-medium">
                            {selectedCompanies.length}
                          </span>
                        )}
                        <span className="sr-only">Filter by Company</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64" align="start">
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        <div className="font-medium text-sm mb-3">
                          Filter by Company
                        </div>
                        {uniqueCompanies.map((company) => (
                          <div
                            key={company}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`company-${company}`}
                              checked={selectedCompanies.includes(company)}
                              onCheckedChange={() => toggleCompany(company)}
                            />
                            <label
                              htmlFor={`company-${company}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {company}
                            </label>
                          </div>
                        ))}
                        {selectedCompanies.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full mt-2"
                            onClick={() => setSelectedCompanies([])}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center whitespace-nowrap">
                  Status
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ml-2 relative ${
                          selectedStatuses.length > 0
                            ? "text-primary"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Filter className="h-4 w-4" />
                        {selectedStatuses.length > 0 && (
                          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-medium">
                            {selectedStatuses.length}
                          </span>
                        )}
                        <span className="sr-only">Filter by Status</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56" align="start">
                      <div className="space-y-2">
                        <div className="font-medium text-sm mb-3">
                          Filter by Status
                        </div>
                        {allStatuses.map((status) => (
                          <div
                            key={status}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`status-${status}`}
                              checked={selectedStatuses.includes(status)}
                              onCheckedChange={() => toggleStatus(status)}
                            />
                            <label
                              htmlFor={`status-${status}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {formatStatusLabel(status)}
                            </label>
                          </div>
                        ))}
                        {selectedStatuses.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full mt-2"
                            onClick={() => setSelectedStatuses([])}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center whitespace-nowrap">
                  Created Date
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-2 text-muted-foreground hover:text-foreground"
                    onClick={() => handleSort("createdAt")}
                  >
                    {getSortIcon("createdAt")}
                    <span className="sr-only">Sort by Created Date</span>
                  </Button>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center whitespace-nowrap">
                  Last Updated
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-2 text-muted-foreground hover:text-foreground"
                    onClick={() => handleSort("updatedAt")}
                  >
                    {getSortIcon("updatedAt")}
                    <span className="sr-only">Sort by Last Updated</span>
                  </Button>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center whitespace-nowrap">
                  Submissions
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-2 text-muted-foreground hover:text-foreground"
                    onClick={() => handleSort("submissions")}
                  >
                    {getSortIcon("submissions")}
                    <span className="sr-only">Sort by Submissions</span>
                  </Button>
                </div>
              </TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-10 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredProjects.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  {searchTerm
                    ? "No projects match your search"
                    : "No projects found"}
                </TableCell>
              </TableRow>
            ) : (
              sortedProjects.map((project) => (
                <TableRow
                  key={project._id}
                  ref={(el) => (rowRefs.current[project._id] = el)}
                  className={`transition-all cursor-pointer hover:bg-muted/30 border-border/40 ${
                    highlightedProjectId === project._id ? "animate-blink bg-primary/5" : ""
                  }`}
                  onClick={() => navigate(`/projects/${project._id}`)}
                >
                  <TableCell className="font-medium pl-6">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                        <Building className="h-4 w-4" />
                      </div>
                      <span className="group-hover:text-primary transition-colors">{project.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {project.ahj ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        <span className="text-sm">
                          {project.ahj.name} ({project.ahj.state})
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{project.createdBy.companyName || "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 items-start">
                      <Badge variant={getStatusBadgeVariant(project.status)}>
                        {project.status.charAt(0).toUpperCase() +
                          project.status.slice(1)}
                      </Badge>
                      {(() => {
                        const daysRemaining = calculateDaysUntilReview(
                          project.estimatedReviewDateByAHJ
                        );
                        if (daysRemaining !== null && daysRemaining >= 0) {
                          return (
                            <Badge
                              variant={getReviewDateBadgeVariant(daysRemaining)}
                              className="text-xs font-normal"
                            >
                              {daysRemaining === 0
                                ? "Review Today"
                                : daysRemaining === 1
                                ? "Review Tomorrow"
                                : `${daysRemaining}d AHJ Review`}
                            </Badge>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(project.createdAt)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(project.updatedAt)}</TableCell>
                  <TableCell
                    ref={(el) => (statusRefs.current[project._id] = el)}
                    className="text-center"
                  >
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-muted text-xs font-medium">
                      {project.submission ? 1 : 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${project._id}`);
                      }}
                    >
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ProjectsTable;
